import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Image, ScrollView, TouchableOpacity, View } from 'react-native';
import Text from 'design-system/Text';
import { CardView, Spacer } from 'design-system';
import { rem } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import useColorScheme from 'hooks/useColorScheme';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { formatPercentageToMinimum2DecimalPlaces } from 'utils';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import SectionHeader from './SectionHeader';
import { getFundAllocations } from '../actions';
const SECTION_KEYS = [
    { key: 'topHoldings', label: 'Holdings' },
    { key: 'assetAllocation', label: 'Assets' },
    { key: 'regions', label: 'Regions', seeAll: true },
    { key: 'sectors', label: 'Sectors', seeAll: true },
    { key: 'currencies', label: 'Currencies', seeAll: true },
];
const VISIBLE_ITEMS = 4;
const lsegLight = require('../img/lseg.png');
const lsegDark = require('../img/lseg_dark.png');
const FundAllocationSection = ({ assetId }) => {
    const styles = useStyles(styleSet);
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const colorScheme = useColorScheme();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const breakdown = useAppSelector((state) => state.invest.fundAllocations?.[assetId]);
    useEffect(() => {
        dispatch(getFundAllocations(assetId));
    }, [assetId]);
    const sections = useMemo(() => {
        if (!breakdown)
            return [];
        return SECTION_KEYS.filter((s) => breakdown[s.key]?.items?.length).map((s) => ({
            label: s.label,
            seeAll: s.seeAll ?? false,
            items: breakdown[s.key].items,
        }));
    }, [breakdown]);
    const selectedSection = sections[selectedIndex];
    const visibleItems = useMemo(() => selectedSection?.items.slice(0, VISIBLE_ITEMS) ?? [], [selectedSection]);
    const hasMore = (selectedSection?.items.length ?? 0) > VISIBLE_ITEMS;
    const handleSeeMore = useCallback(() => {
        if (!selectedSection)
            return;
        navigation.navigateDeprecated('InvestFundAllocationDetail', {
            title: selectedSection.label,
            items: selectedSection.items,
        });
    }, [navigation, selectedSection]);
    if (!sections.length)
        return null;
    return (<>
      <SectionHeader title="Fund allocation"/>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.tabContainer}>
        {sections.map((section, index) => {
            const isSelected = index === selectedIndex;
            return (<TouchableBounce key={section.label} style={[styles.sectionButton, isSelected && styles.selectedButton]} onPress={() => setSelectedIndex(index)}>
              <Text Text-14 {...(isSelected ? { OnBrand: true } : { Secondary: true })}>
                {section.label}
              </Text>
            </TouchableBounce>);
        })}
      </ScrollView>
      <Spacer h={16}/>
      <CardView noMarginTop noMarginBottom>
        <View style={styles.content}>
          {visibleItems.map((item, index) => (<AllocationRow key={`${item.name}-${index}`} item={item}/>))}
          <View style={styles.dataProviderRow}>
            <View style={styles.flex}>
              <Text TextThin-14 Secondary>
                Data provided by
              </Text>
            </View>
            <Spacer w={8}/>
            <Image source={colorScheme === 'dark' ? lsegLight : lsegDark} style={styles.lsegLogo} resizeMode="contain"/>
          </View>
          {hasMore ? (<>
              <Spacer h={8}/>
              <TouchableOpacity onPress={handleSeeMore} style={styles.seeMore}>
                <Text Text-16 Primary centered>
                  {selectedSection.seeAll ? 'See all' : 'See more'} {selectedSection.label.toLowerCase()}
                </Text>
              </TouchableOpacity>
            </>) : (<Spacer h={12}/>)}
        </View>
      </CardView>
    </>);
};
const AllocationRow = memo(({ item }) => {
    const styles = useStyles(styleSet);
    return (<View style={styles.row}>
      <View style={styles.flex}>
        <Text Text-14>{item.name}</Text>
      </View>
      <Text NumbersThin-14 Secondary>
        {formatPercentageToMinimum2DecimalPlaces(item.percentage)}
      </Text>
    </View>);
});
const styleSet = createStyleSheets((colors) => ({
    sectionButton: {
        borderRadius: rem(8),
        paddingVertical: rem(6),
        marginHorizontal: rem(4),
        paddingHorizontal: rem(16),
    },
    selectedButton: {
        backgroundColor: colors.buttons.brandGroup,
    },
    content: {
        padding: rem(16),
        paddingVertical: rem(4),
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: rem(12),
        gap: rem(16),
    },
    lsegLogo: {
        width: rem(80),
        height: rem(24),
    },
    seeMore: {
        paddingBottom: rem(12),
    },
    flex: {
        flex: 1,
    },
    dataProviderRow: {
        marginTop: rem(12),
        flexDirection: 'row',
        alignItems: 'center',
    },
    tabContainer: {
        paddingHorizontal: rem(12),
    },
}));
export default memo(FundAllocationSection);
